<template>
  <main
    class="login-page min-h-screen w-full relative text-gray-900 dark:text-gray-100"
    style="font-family: 'Inter', sans-serif;"
  >
    <div class="login-page__content min-h-screen flex items-center justify-center px-4 py-10 relative z-10">
      <div class="w-full max-w-md">
        <div class="bg-white border border-gray-200 rounded-xl  shadow-sm px-10 py-10 sm:p-8 dark:border-white/10 dark:bg-white/5">
          <div class="flex items-center justify-center gap-2.5 mb-6">
            <img class="h-9 w-auto" src="../assets/logo.png" alt="" />
          
          </div>

          <div class="text-center mb-6">

            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              Please sign-in to your account to continue.
            </p>
          </div>

          <form @submit.prevent="login">
            <div class="space-y-5">
              <CTailwindInput
                id="login-username"

                label="Username"
                :value="username"
                type="text"
                name="username"
                autocomplete="username"
                placeholder="Username"
                @input="username = $event"
              />

              <div class="relative">
                <CTailwindInput
                  id="login-password"
                  label="Password"
                  :value="password"
                  :type="showPassword ? 'text' : 'password'"
                  name="password"
                  autocomplete="current-password"
                  placeholder="Password"

                  class="pr-10 mt-5"
                  @input="password = $event"
                />
                <button
                  class="absolute right-0 top-6 flex h-10 w-10 items-center justify-center text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                  type="button"
                  aria-label="Toggle password visibility"
                  @click="showPassword = !showPassword"
                >
                  <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'" />
                </button>
              </div>
            </div>



            <c-tailwind-btn
              type="submit"
              variant="primary"
              class="mt-6 flex min-h-[2.75rem] w-full items-center justify-center py-2.5"
              :disabled="!isFormValid || loading"
            >
              <span v-if="loading" class="inline-flex items-center gap-2">
                <span
                  class="h-4 w-4 shrink-0 rounded-full border-2 border-white/35 border-t-white animate-spin"
                />
                Signing in…
              </span>
              <span v-else>Login</span>
            </c-tailwind-btn>
          </form>

          <p class="text-center text-sm text-gray-600 dark:text-gray-400 mt-6">
            New on our platform?
            <a
              class="font-medium text-gray-900 hover:underline dark:text-gray-100"
              href="mailto:info@MetalNx.com?subject=New%20account"
            >
              Get in touch.
            </a>
          </p>

          <div class="mt-8 pt-6 border-t border-gray-100 text-xs text-gray-500 text-center dark:border-white/10 dark:text-gray-400">
            © 2026 MetalNx. All rights reserved. Contact: info@metalnx.com
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { authenticate } from '@/api/users'
import CTailwindInput from '@/components/Vuetify/CTailwindInput.vue'
import CTailwindBtn from '@/components/Vuetify/CTailwindBtn.vue'
import { setPendingUserTour, hasCompletedUserTour } from '@/utils/userTour'

export default {
  name: "Login",
  components: {
    CTailwindInput,
    CTailwindBtn,
  },
  data() {
    return {
      showPassword: false,
      rememberMe: false,
      username: "",
      password: "",
      confirmPassword: "",
      errorMessage: "",
      loading: false,
    };
  },
  mounted() {
    const saved = localStorage.getItem("loginRememberUsername");
    if (saved) {
      this.username = saved;
      this.rememberMe = true;
    }
  },
  methods: {
    login() {
      if (!this.isFormValid || this.loading) return

      this.loading = true
      const _this = this

      authenticate(
        this.username,
        this.password,
        function (res) {
          if (_this.rememberMe) {
            localStorage.setItem("loginRememberUsername", _this.username);
          } else {
            localStorage.removeItem("loginRememberUsername");
          }
          _this.$root.notify.show({message: 'Welcome ' + _this.username + "!"})
          _this.$store.commit('login', res)

          if (res.is_staff !== true) {
            if (!hasCompletedUserTour(res.user)) {
              setPendingUserTour(true)
            }
          }

          if (_this.$route.query.redirect != null) {
            _this.$router.push(_this.$route.query.redirect)
          } else {
            if (res.is_staff === true) {
              _this.$router.push('/admin')
            } else {
              _this.$router.push('/user/home')
            }
          }

          _this.loading = false
        },
        function (err) {
          _this.$root.notify.show({message: "Invalid username or password", type: "error"})
          console.log(err)
          _this.loading = false
        }
      )
    }
  },
  computed: {
    isFormValid() {
      return Boolean(this.username && this.password)
    }
  }
};
</script>

<style scoped>
/*
  Light: single smooth gradient (matches dark-mode approach).
  Avoids grainy noise overlay and harsh corner color blobs.
*/
.login-page {
  background-color: hsl(210 22% 98%);
}

.login-page::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-color: hsl(210 22% 98%);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-blend-mode: normal;
  background-image: radial-gradient(
    ellipse 140% 85% at 50% 100%,
    hsl(174 38% 91%) 0%,
    hsl(196 28% 95%) 38%,
    hsl(210 22% 98%) 100%
  );
}

/*
  Dark: single symmetric gradient — avoids side-by-side color bands from multiple
  bottom-anchored radials (looked like a “split” / two-piece background).
*/
html.dark .login-page {
  background-color: hsl(218 24% 8%);
}

html.dark .login-page::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-color: hsl(218 24% 8%);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-blend-mode: normal;
  background-image: radial-gradient(
    ellipse 140% 85% at 50% 100%,
    hsl(180 22% 14%) 0%,
    hsl(218 26% 9%) 42%,
    hsl(220 20% 7%) 100%
  );
}
</style>